import { UrlObject } from "url";

import { getPaymentsPagePaymentId } from "./components/payments/paymentsUtils";

/* eslint-disable @typescript-eslint/no-explicit-any */
type ArgsOf<T extends (routeParams: any, ...otherArgs: any[]) => any> = T extends (
    routeParams: infer Args,
    ...otherArgs: any[]
) => any
    ? Args
    : never;

type EmptyObject = Record<string, never>;
type WithAdditionalArgs<
    ParentRoute extends (routeParams: any, ...otherArgs: any[]) => any,
    AdditionalArgs = EmptyObject,
> = AdditionalArgs extends EmptyObject ? ArgsOf<ParentRoute> : ArgsOf<ParentRoute> & AdditionalArgs;
/* eslint-enable @typescript-eslint/no-explicit-any */

export namespace Routes {
    export function AI(): string {
        return "/ai";
    }

    export namespace AI {
        export function Chat({ chatId, boardId }: { chatId: string; boardId?: string }): string {
            return `${AI()}/${chatId}${boardId ? `#${boardId}` : ""}`;
        }
    }

    export function Accounts(): string {
        return "/accounts";
    }
    export namespace Accounts {
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Accounts()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                Internal = "internal",
                Connected = "connected",
                Statements = "statements",
            }
        }

        export function Account({ accountId }: { accountId: string }): string {
            return `${Accounts()}/${accountId}`;
        }
        export namespace Account {
            export function EditAutoPay({ ...accountArgs }: WithAdditionalArgs<typeof Account>): string {
                return `${Account(accountArgs)}/edit`;
            }

            export function PayCreditAccount({ ...accountArgs }: WithAdditionalArgs<typeof Account>): string {
                return `${Account(accountArgs)}/pay`;
            }
        }

        export function Apply(): string {
            return `${Accounts()}/apply`;
        }
    }

    export function Activities(): string {
        return "/activities";
    }
    export namespace Activities {
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Activities()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                Upcoming = "upcoming",
                Completed = "completed",
                Failed = "failed",
            }
        }
    }

    export function Payments(): string {
        return "/payments";
    }

    export namespace Payments {
        export function Details({ paymentId, ordinal }: { paymentId: string; ordinal?: number }): UrlObject {
            return {
                pathname: Payments(),
                query: { paymentId: getPaymentsPagePaymentId({ id: paymentId, ordinal }) },
            };
        }
        export function List({ tab, bulkPaymentId }: { tab: List.Tab; bulkPaymentId?: string }): string {
            return `${Payments()}/${tab}` + (bulkPaymentId ? `?bulkPaymentId=${bulkPaymentId}` : "");
        }
        export namespace List {
            export enum Tab {
                PendingApproval = "pending-approval",
                PendingRelease = "pending-release",
                AwaitingFunds = "awaiting-funds",
                Scheduled = "scheduled",
                Completed = "completed",
                Canceled = "canceled",
                Bulk = "bulk",
            }
        }
    }

    export function Ledger(): string {
        return "/ledger";
    }

    export function Cards(): string {
        return "/cards";
    }
    export namespace Cards {
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Cards()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                MyCards = "my-cards",
                DebitCards = "debit",
                CreditCards = "credit",
                Transactions = "transactions",
            }
        }

        export function Create(): string {
            return `${Cards()}/create`;
        }

        export function Card({ cardId }: { cardId: string }): string {
            return `${Cards()}/${cardId}`;
        }
        export namespace Card {
            export function Edit({ ...cardArgs }: WithAdditionalArgs<typeof Card>): string {
                return `${Card(cardArgs)}/edit`;
            }
        }
    }

    export function Contacts(): string {
        return "/contacts";
    }
    export namespace Contacts {
        export function Contact({ recipientId }: { recipientId: string }): UrlObject {
            return {
                pathname: Contacts.List({ tab: Contacts.List.Tab.Recipients }),
                query: { recipientId },
            };
        }
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Contacts()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                Recipients = "recipients",
                Rejected = "rejected",
                Requested = "requested",
                Invited = "invited",
            }
        }

        export function AddRecipient(): string {
            return `${Contacts.List({ tab: Contacts.List.Tab.Recipients })}/add`;
        }
        export namespace AddRecipient {
            export function Bulk(): string {
                return `${AddRecipient()}/bulk`;
            }
        }
    }

    export function Documents(): string {
        return "/documents";
    }

    export function Transfers(): string {
        return "/transfers";
    }
    export namespace Transfers {
        export function Internal(): string {
            return `${Transfers()}/internal`;
        }

        export function DepositCheck(): string {
            return `${Transfers()}/deposit-check`;
        }

        // TODO: Use the GQL type as the type of query once it is defined.
        export function Transfer(query?: {
            recipientId?: string;
            destinationAmount?: number;
            reason?: string;
            reference?: string;
            purpose?: string;
            scheduledDate?: string;
        }): UrlObject {
            return {
                pathname: `${Transfers()}/transfer`,
                query,
            };
        }
        export namespace Transfer {
            export function Bulk(): string {
                return `${Transfer().pathname}/bulk`;
            }
        }
    }

    export function ContactUs(): string {
        return "/contact-us";
    }

    export function Bills(): string {
        return "/bills";
    }
    export namespace Bills {
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Bills()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                Drafts = "drafts",
                All = "all",
            }
        }

        export function Create(): string {
            return `${Bills()}/create`;
        }

        export function Draft({ billId }: { billId: string }): string {
            return `${Bills.List({ tab: Bills.List.Tab.Drafts })}/${billId}`;
        }
    }

    export function Invoices(): string {
        return "/invoices";
    }
    export namespace Invoices {
        export function Create(): string {
            return `${Invoices()}/create`;
        }
    }

    export function Settings(): string {
        return "/settings";
    }
    export namespace Settings {
        export function List({ tab }: { tab: List.Tab }): string {
            return `${Settings()}/${tab}`;
        }
        export namespace List {
            export enum Tab {
                App = "app",
                ApprovalPolicy = "approvals",
                Email = "email",
                Api = "api",
                Team = "team",
            }
        }
    }

    export function Deposit(): string {
        return "/deposit";
    }

    export function Dashboard(): string {
        return "/home";
    }

    export function Orgs(): string {
        return "/orgs";
    }

    export function KycApply(): string {
        return "/apply";
    }
    export namespace KycApply {
        export function Application({ applicationId }: { applicationId: string }): string {
            return `${KycApply()}/${applicationId}`;
        }
        export function Partner({ token }: { token: string }): string {
            return `${KycApply()}/partner?token=${token}`;
        }
    }

    export function Download({ fileId }: { fileId: string }): string {
        return `/download/${fileId}`;
    }

    export function Omi(): string {
        return "/omi";
    }

    export namespace Omi {
        export function Contracts(): string {
            return `${Omi()}/contracts`;
        }

        export namespace Contracts {
            export function Contract({ contractId }: { contractId: string }): string {
                return `${Omi.Contracts()}/${contractId}`;
            }

            export function View({ view }: { view: List.View }): UrlObject | string {
                return view === List.View.Table ? Contracts() : { pathname: Contracts(), query: { view } };
            }

            export namespace List {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                export enum View {
                    Table = "table",
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    Calendar = "calendar",
                }
            }

            export function Missing() {
                return `${Omi.Contracts()}/missing`;
            }
        }

        export function Cashflow(): string {
            return `${Omi()}/cashflow`;
        }

        export function VarianceAnalysis(): string {
            return `${Omi()}/variance`;
        }

        export function Procurement(): string {
            return `${Omi()}/procurement`;
        }

        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function Settings(): string {
            return `${Omi()}/settings`;
        }

        export namespace Settings {
            export function List({ tab }: { tab: List.Tab }): string {
                return tab === List.Tab.Workspace ? Settings() : `${Settings()}/${tab}`;
            }
            export namespace List {
                export enum Tab {
                    Billing = "billing",
                    Integrations = "integrations",
                    Workspace = "workspace",
                }
            }
        }

        export function Feedback({ token }: { token: string }): string {
            return `${Omi()}/feedback/${token}`;
        }

        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function Team(): string {
            return `${Omi()}/team`;
        }

        export namespace Team {
            export function List({ tab }: { tab: List.Tab }): string {
                return tab === List.Tab.Active ? Team() : `${Team()}/${tab}`;
            }
            export namespace List {
                export enum Tab {
                    Active = "active",
                    Inactive = "inactive",
                }
            }
        }

        export function Signup(): string {
            return `${Omi()}/signup`;
        }

        export namespace Signup {
            export function Billing(): string {
                return `${Signup()}/billing`;
            }
        }

        export function Vendors(): string {
            return `${Omi()}/vendors`;
        }

        export namespace Vendors {
            export function Vendor({ vendorId }: { vendorId: string }): string {
                return `${Omi.Vendors()}/${vendorId}`;
            }
        }
    }
}
